import type {
  Company,
  Site,
  Well,
  RrcCompanyProfile,
  RrcLocationProfile,
} from '@ots-frontend/ots-data';

/** Seed new drafts only from matching source records; agency identifiers remain explicit. */
export function companyProfileDefaults(
  company: Company,
  regionId: string
): Partial<RrcCompanyProfile> {
  const regional = company.companyRegionProfileList?.find(
    (p) => p.companyId === company.id && p.regionId === regionId
  );
  return {
    companyId: company.id,
    companyName: company.name,
    orgName: regional?.formalName?.trim() || company.name,
    contactPhone: regional?.phone,
    contactEmail: regional?.emailAddress,
    ...(regional
      ? {
          address: {
            street: regional.address1,
            street2: regional.address2,
            city: regional.city,
            state: regional.state,
            postalCode: regional.zipCode,
          },
        }
      : {}),
  };
}
export function locationProfileDefaults(
  source: Site | Well,
  type: 'SITE' | 'WELL'
): Partial<RrcLocationProfile> {
  return {
    locationId: source.id,
    locationType: type,
    locationName: source.name,
    facilityName: source.name,
    ...(source.address ? { address: { ...source.address } } : {}),
    latCoord: source.latCoord,
    longCoord: source.longCoord,
    leaseName:
      type === 'SITE'
        ? (source as Site).rrcLeaseName || source.leaseName
        : source.leaseName,
    // Generic leaseId can be an internal ID, so only the explicit RRC field is safe.
    ...(type === 'SITE' ? { leaseNumber: (source as Site).rrcLeaseId } : {}),
  };
}

/** Explicit defaults, including empty strings, false and zero, take precedence. */
export function mergeProfileDefaults<
  T extends { address?: RrcCompanyProfile['address'] }
>(seed: T, defaults?: T): T {
  const defined = Object.fromEntries(
    Object.entries(defaults ?? {}).filter(([, value]) => value !== undefined)
  );
  return {
    ...seed,
    ...defined,
    ...(defaults?.address
      ? { address: { ...seed.address, ...defaults.address } }
      : {}),
  };
}
